import {
  Box,
  Button,
  Chip,
  Divider,
  Menu,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useAgentEdit } from 'components/@home/@settings/Tabs/Agents/AgentEditContext';
import IdentityCard from 'components/@home/@settings/Tabs/Agents/IdentityCard';
import InstructionSaveFooter from 'components/@home/@settings/Tabs/Agents/InstructionSaveFooter';
import MetricRuleGroupCard from 'components/@home/@settings/Tabs/Agents/metricPanels/MetricRuleGroupCard';
import AgentMetric from 'services/api/AgentMetric';
import ChartLineIcon from 'mdi-react/ChartLineIcon';
import DotsHorizontalIcon from 'mdi-react/DotsHorizontalIcon';
import MinusIcon from 'mdi-react/MinusIcon';
import PlusIcon from 'mdi-react/PlusIcon';
import RefreshIcon from 'mdi-react/RefreshIcon';
import TrashCanOutlineIcon from 'mdi-react/TrashCanOutlineIcon';
import { arrayOf, bool, func, object, string } from 'prop-types';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { buildAgenticPath } from 'utils/agenticRoute';
import { I18n } from 'utils/i18n';

const emptyDraft = () => ({ name: '', description: '', rules: [], exclusionRules: [] });

const draftFromMetric = metric =>
  metric
    ? {
        name: metric.name || '',
        description: metric.description || '',
        rules: metric.rules || [],
        exclusionRules: metric.exclusionRules || [],
      }
    : emptyDraft();

const getStyles = theme => ({
  editorRoot: {
    flex: '1 1 0',
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    backgroundColor: theme.palette.background.paper,
  },
  body: {
    flex: '1 1 0',
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  chrome: {
    flexShrink: 0,
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
    paddingTop: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  moreActionsButton: {
    borderColor: theme.palette.divider,
    minWidth: 0,
  },
  content: {
    flex: '1 1 0',
    minHeight: 0,
    overflowY: 'auto',
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(3),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2.5),
  },
  rulesSection: { display: 'flex', flexDirection: 'column', gap: theme.spacing(2) },
  helper: { fontSize: theme.typography.pxToRem(12), color: theme.palette.text.secondary },
  addGroupBtn: { alignSelf: 'stretch', textTransform: 'none' },
  preview: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: '8px',
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
  },
  previewHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  fieldLabel: { fontSize: theme.typography.pxToRem(13), fontWeight: 600 },
  chips: { display: 'flex', gap: theme.spacing(1), flexWrap: 'wrap' },
  rate: { fontSize: theme.typography.pxToRem(28), fontWeight: 700 },
});

export const MetricEditorForm = ({
  metric = null,
  tags = [],
  onSaved,
  onClose,
  onDetach = null,
  onDeleted = null,
  active = true,
  closeOnSave = false,
}) => {
  const theme = useTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);
  const isEdit = Boolean(metric?._id);
  const [draft, setDraft] = useState(emptyDraft);
  const [tab, setTab] = useState('rules');
  const [saving, setSaving] = useState(false);
  const [saveFeedback, setSaveFeedback] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [identityAnchor, setIdentityAnchor] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const initialRef = useRef(emptyDraft());
  const saveFeedbackTimeoutRef = useRef(null);

  const clearSaveFeedbackLater = useCallback(() => {
    if (saveFeedbackTimeoutRef.current) {
      clearTimeout(saveFeedbackTimeoutRef.current);
    }
    saveFeedbackTimeoutRef.current = setTimeout(() => {
      setSaveFeedback(null);
      saveFeedbackTimeoutRef.current = null;
    }, 3000);
  }, []);

  useEffect(
    () => () => {
      if (saveFeedbackTimeoutRef.current) {
        clearTimeout(saveFeedbackTimeoutRef.current);
      }
    },
    [],
  );

  useEffect(() => {
    if (active) {
      const next = draftFromMetric(metric);
      setDraft(next);
      initialRef.current = next;
      setTab('rules');
      setSaveFeedback(null);
      setPreview(null);
    }
  }, [active, metric]);

  const dirty = useMemo(
    () => JSON.stringify(initialRef.current) !== JSON.stringify(draft),
    [draft],
  );
  const valid = draft.name.trim().length > 0;
  const key = tab === 'rules' ? 'rules' : 'exclusionRules';
  const groups = draft[key];

  useEffect(() => {
    if (dirty && saveFeedback?.severity === 'success') {
      setSaveFeedback(null);
    }
  }, [dirty, saveFeedback?.severity]);

  const update = patch => setDraft(prev => ({ ...prev, ...patch }));
  const updateGroup = (idx, next) =>
    update({ [key]: groups.map((g, i) => (i === idx ? next : g)) });
  const removeGroup = idx => update({ [key]: groups.filter((_, i) => i !== idx) });
  const addGroup = () => update({ [key]: [...groups, { operator: 'and', conditions: [] }] });

  const refreshPreview = async () => {
    if (!isEdit) return;
    setPreviewLoading(true);
    try {
      const result = await AgentMetric.evaluate(metric._id, {
        rules: draft.rules,
        exclusionRules: draft.exclusionRules,
      });
      setPreview(result);
    } catch {
      setPreview(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSave = async () => {
    if (!valid || saving || !dirty) return;
    setSaving(true);
    setSaveFeedback(null);
    const body = {
      name: draft.name.trim(),
      description: draft.description.trim(),
      rules: draft.rules,
      exclusionRules: draft.exclusionRules,
    };
    try {
      const saved = isEdit
        ? await AgentMetric.update(metric._id, body)
        : await AgentMetric.create(body);
      onSaved(saved, { created: !isEdit });
      initialRef.current = draftFromMetric(saved);
      setSaveFeedback({
        severity: 'success',
        message: I18n.t('AgenticChatFlow.ToolSaved'),
      });
      clearSaveFeedbackLater();
      if (closeOnSave) onClose();
    } catch (e) {
      setSaveFeedback({
        severity: 'error',
        message: e?.response?.data?.error?.message || I18n.t('AgenticChatFlow.MetricsSaveError'),
      });
      clearSaveFeedbackLater();
    } finally {
      setSaving(false);
    }
  };

  const handleDetach = () => {
    setMenuAnchor(null);
    if (!metric?._id || !onDetach) return;
    onDetach(metric._id);
    onClose();
  };

  const handleDelete = async () => {
    setMenuAnchor(null);
    if (!metric?._id) return;

    if (!window.confirm(I18n.t('AgenticChatFlow.MetricsDeleteConfirm', { name: metric.name })))
      return;
    try {
      await AgentMetric.destroy(metric._id);
      if (onDeleted) onDeleted(metric._id);
      onClose();
    } catch (e) {
      setSaveFeedback({
        severity: 'error',
        message: e?.response?.data?.error?.message || I18n.t('AgenticChatFlow.MetricsDeleteError'),
      });
      clearSaveFeedbackLater();
    }
  };

  const displayTitle =
    draft.name.trim() ||
    (isEdit
      ? I18n.t('AgenticChatFlow.MetricsEditTitle')
      : I18n.t('AgenticChatFlow.MetricsNewTitle'));

  return (
    <Box sx={styles.editorRoot}>
      <Box sx={styles.body}>
        <Box sx={styles.chrome}>
          <IdentityCard
            icon={<ChartLineIcon size={22} />}
            title={displayTitle}
            description={draft.description.trim() || null}
            emptyDescription={I18n.t('AgenticChatFlow.NoDescription')}
            divider
            edit={{
              open: Boolean(identityAnchor),
              anchorEl: identityAnchor,
              onOpen: e => setIdentityAnchor(e.currentTarget),
              onClose: () => setIdentityAnchor(null),
              content: (
                <>
                  <TextField
                    value={draft.name}
                    onChange={e => update({ name: e.target.value })}
                    size="small"
                    fullWidth
                    autoFocus
                    label={I18n.t('AgenticChatFlow.MetricsFieldName')}
                  />
                  <TextField
                    value={draft.description}
                    onChange={e => update({ description: e.target.value })}
                    size="small"
                    fullWidth
                    multiline
                    minRows={3}
                    label={I18n.t('AgenticChatFlow.MetricsFieldDescription')}
                  />
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{
                      justifyContent: 'flex-end',
                    }}
                  >
                    <Button onClick={() => setIdentityAnchor(null)} color="inherit">
                      {I18n.t('AgenticChatFlow.Cancel')}
                    </Button>
                    <Button
                      onClick={() => setIdentityAnchor(null)}
                      variant="contained"
                      color="primary"
                      disableElevation
                      disabled={!draft.name.trim()}
                    >
                      {I18n.t('AgenticChatFlow.Save')}
                    </Button>
                  </Stack>
                </>
              ),
            }}
            actions={
              isEdit ? (
                <>
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={e => setMenuAnchor(e.currentTarget)}
                    size="small"
                    sx={styles.moreActionsButton}
                  >
                    <DotsHorizontalIcon size={20} />
                  </Button>
                  <Menu
                    anchorEl={menuAnchor}
                    open={Boolean(menuAnchor)}
                    onClose={() => setMenuAnchor(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  >
                    {onDetach && (
                      <MenuItem onClick={handleDetach}>
                        <MinusIcon size={16} style={{ marginRight: 8 }} />
                        {I18n.t('AgenticChatFlow.Detach')}
                      </MenuItem>
                    )}
                    {onDetach && <Divider />}
                    <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
                      <TrashCanOutlineIcon size={16} style={{ marginRight: 8 }} />
                      {I18n.t('AgenticChatFlow.Delete')}
                    </MenuItem>
                  </Menu>
                </>
              ) : null
            }
          />
        </Box>
        <Box sx={styles.content}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)}>
            <Tab value="rules" label={I18n.t('AgenticChatFlow.MetricsRulesTab')} />
            <Tab value="exclusions" label={I18n.t('AgenticChatFlow.MetricsExclusionsTab')} />
          </Tabs>

          <Box sx={styles.rulesSection}>
            <Typography sx={styles.helper}>
              {tab === 'rules'
                ? I18n.t('AgenticChatFlow.MetricsRulesHelper')
                : I18n.t('AgenticChatFlow.MetricsExclusionsHelper')}
            </Typography>
            {groups.map((group, idx) => (
              <MetricRuleGroupCard
                key={idx}
                group={group}
                index={idx}
                tags={tags}
                onChange={next => updateGroup(idx, next)}
                onRemove={() => removeGroup(idx)}
              />
            ))}
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<PlusIcon size={16} />}
              sx={styles.addGroupBtn}
              onClick={addGroup}
            >
              {I18n.t('AgenticChatFlow.MetricsAddRuleGroup')}
            </Button>
          </Box>

          {isEdit && (
            <Box sx={styles.preview}>
              <Box sx={styles.previewHeader}>
                <Typography sx={styles.fieldLabel}>
                  {I18n.t('AgenticChatFlow.MetricsPreviewTitle')}
                </Typography>
                <Button
                  size="small"
                  startIcon={<RefreshIcon size={16} />}
                  onClick={refreshPreview}
                  disabled={previewLoading}
                >
                  {I18n.t('AgenticChatFlow.MetricsPreviewRefresh')}
                </Button>
              </Box>
              {preview ? (
                <>
                  <Box sx={styles.chips}>
                    <Chip
                      label={`${I18n.t('AgenticChatFlow.MetricsStateTrue')}: ${preview.true}`}
                      color="success"
                      variant="outlined"
                    />
                    <Chip
                      label={`${I18n.t('AgenticChatFlow.MetricsStateFalse')}: ${preview.false}`}
                      color="error"
                      variant="outlined"
                    />
                    <Chip
                      label={`${I18n.t('AgenticChatFlow.MetricsStateExcluded')}: ${
                        preview.excluded
                      }`}
                    />
                  </Box>
                  <Typography sx={styles.rate}>{`${(preview.rate * 100).toFixed(1)}%`}</Typography>
                </>
              ) : (
                <Typography sx={styles.helper}>
                  {I18n.t('AgenticChatFlow.MetricsPreviewEmpty')}
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </Box>
      <InstructionSaveFooter
        dirty={dirty}
        saving={saving}
        saveFeedback={saveFeedback}
        onSave={handleSave}
        saveDisabled={!valid}
      />
    </Box>
  );
};

MetricEditorForm.propTypes = {
  metric: object,
  tags: arrayOf(object),
  onSaved: func.isRequired,
  onClose: func.isRequired,
  onDetach: func,
  onDeleted: func,
  active: bool,
  closeOnSave: bool,
};

const MetricEditor = ({ metricId }) => {
  const navigate = useNavigate();
  const { flow, authCompany, agent, metrics, tags, onSaveAgent, onReloadMetrics } = useAgentEdit();

  const isNew = metricId === 'new';
  const metric = useMemo(() => {
    if (isNew) return null;
    return (metrics || []).find(m => String(m._id) === String(metricId)) || null;
  }, [isNew, metricId, metrics]);

  const attachedIds = useMemo(() => (agent?.metricIds || []).map(String), [agent?.metricIds]);

  const goBack = () => {
    if (!flow?._id) return;
    navigate(buildAgenticPath(authCompany, { flowId: flow._id, section: 'metrics' }));
  };

  const handleSaved = async (saved, { created }) => {
    if (created) {
      onSaveAgent({ metricIds: Array.from(new Set([...attachedIds, String(saved._id)])) });
      await onReloadMetrics?.();
      navigate(
        buildAgenticPath(authCompany, {
          flowId: flow._id,
          section: 'metrics',
          metricId: saved._id,
        }),
      );
      return;
    }
    await onReloadMetrics?.();
  };

  const handleDetach = id => {
    onSaveAgent({ metricIds: attachedIds.filter(mid => mid !== String(id)) });
  };

  const handleDeleted = id => {
    if (attachedIds.includes(String(id))) {
      onSaveAgent({ metricIds: attachedIds.filter(mid => mid !== String(id)) });
    }
    onReloadMetrics?.();
    goBack();
  };

  if (!isNew && !metric) return null;

  return (
    <MetricEditorForm
      metric={metric}
      tags={tags}
      onSaved={handleSaved}
      onClose={goBack}
      onDetach={!isNew ? handleDetach : null}
      onDeleted={!isNew ? handleDeleted : null}
    />
  );
};

MetricEditor.propTypes = {
  metricId: string.isRequired,
};

export default MetricEditor;
